"use client";
import { useState, useEffect, useSyncExternalStore } from "react";
import { Cookie, X } from "lucide-react";
import Link from "next/link";
import { Button } from "@/app/components/shadcn/button";
import { saveConsent, hasConsentDecision } from "@/app/lib/tracking/consent";
import { CONSENT_VERSION } from "@/app/lib/tracking/types";
import useMobile from "@/app/lib/features/mobile/mobile.hook";
import { trackGeneric } from "@/app/lib/tracking/interactions";

/**
 * Banner'ın görünürlüğü consent KAYDINDAN AYRI bir anahtarda tutuluyor.
 *
 * Eskiden `getSnapshot` doğrudan consent kaydına bakıyordu. Mount'taki otomatik
 * kabul o kaydı hemen yazdığı için `shouldShow`, 500ms'lik `ready` gecikmesi
 * dolmadan false'a düşüyordu — `visible = ready && shouldShow` ikisi hiçbir
 * zaman aynı anda true olmadığından banner HİÇBİR ziyaretçide açılmıyordu.
 *
 * Değer olarak CONSENT_VERSION yazılıyor: sürüm yükseltilince banner yeniden
 * gösterilir, ayrı bir temizlik koduna gerek kalmaz.
 */
const BANNER_SEEN_KEY = "cookie_banner_seen";

const subscribe = (callback: () => void) => {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
};

const getSnapshot = (): boolean => {
  try {
    return localStorage.getItem(BANNER_SEEN_KEY) !== CONSENT_VERSION;
  } catch {
    // localStorage okunamıyorsa yazılamaz da — kapatılamayan bir banner
    // göstermektense hiç göstermemek daha az zararlı.
    return false;
  }
};

const getServerSnapshot = (): boolean => false;

const CookieBanner = () => {
  const { isPurePage } = useMobile();
  const shouldShow = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [dismissed, setDismissed] = useState(false);
  const [ready, setReady] = useState(false);

  // Giriş animasyonu için kısa gecikme.
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // Sayfaya girildiği anda varsayılan olarak tam kabul yazılır — banner'daki
  // etkileşimden BAĞIMSIZ ve bilinçli bir ürün kararı. Gerekçesi ve ilk hit'e
  // etkisi için bkz. app/lib/tracking/consent-mode.ts.
  // Kararını daha önce vermiş kullanıcı (ör. /cerez-tercihlerimi-yonet üzerinden
  // reddetmiş) ezilmez: kayıt varsa dokunulmaz.
  useEffect(() => {
    if (!hasConsentDecision()) {
      saveConsent({ analytics: true, marketing: true, functional: true });
    }
  }, []);

  const visible = ready && shouldShow && !dismissed && !isPurePage;

  // Banner bir BİLGİLENDİRME olarak konumlandırıldı: kapatmak consent'i
  // DEĞİŞTİRMEZ, yalnızca "gösterildi" bilgisini kalıcılaştırır. Her iki buton
  // da aynı yolu izler — giriş anındaki kabul geçerli kalmaya devam eder.
  const dismiss = () => {
    try {
      localStorage.setItem(BANNER_SEEN_KEY, CONSENT_VERSION);
    } catch {}
    setDismissed(true);
  };

  return (
    <div
      className={`fixed bottom-4 left-4 right-4 z-[999] sm:left-auto sm:right-4 sm:max-w-sm md:max-w-md transition-[transform,opacity] duration-300 ease-out ${
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-[110%] opacity-0 pointer-events-none"
      }`}
    >
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 sm:p-5">
        {/* Çarpı da butonlarla aynı yolu izler: kapatır, consent'e dokunmaz. */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Kapat"
          className="absolute top-3 right-3 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-2 mb-2 pr-8">
          <div className="p-1.5 bg-main/10 rounded-lg shrink-0">
            <Cookie size={18} className="text-main" />
          </div>
          <p className="font-semibold text-sm text-gray-900">Çerez Politikası</p>
        </div>

        <p className="text-xs text-gray-500 leading-relaxed mb-4">
          Sitemizi daha iyi hale getirmek, kişiselleştirilmiş içerik ve reklamlar sunmak
          için çerezler kullanıyoruz. Detaylı bilgi için{" "}
          <a
            href="/gizlilik"
            onClick={() => trackGeneric("agreement_gizlilik", "click")}
            className="text-main underline underline-offset-2 hover:opacity-80"
            target="_blank"
            rel="noopener noreferrer"
          >
            Gizlilik Politikası
          </a>
          &apos;nı inceleyebilirsiniz.
        </p>

        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-9 text-xs"
              onClick={dismiss}
            >
              Reddet
            </Button>
            <Button
              size="sm"
              className="flex-1 h-9 text-xs"
              onClick={dismiss}
            >
              Tümünü Kabul Et
            </Button>
          </div>
          <Link
            href="/cerez-tercihlerimi-yonet"
            onClick={() => {
              trackGeneric("cookie_preferences", "click");
              dismiss();
            }}
            className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2 mt-1 text-center"
          >
            Tercihlerimi Yönet
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
